import { getApiData } from "../utils/APIHelper";

const RFQ_ENDPOINTS = {
    GET_BID_LIST: "api/bid/list",
    GET_BID_STATS: "api/contractor/bid-stats",
    GET_BID_DETAILS: (projectId, bidId, grouping = null) => {
        const baseUrl = `api/scope/${projectId}/bid/${bidId}`;
        return grouping ? `${baseUrl}?grouping=${grouping}` : baseUrl;
    },
    GET_PROJECT_ROOM_BOQS: (projectId) => `api/project/${projectId}/rooms/boqs`,
    GET_BILLING_MILESTONES: (bidId) => `api/bid/${bidId}/billing-milestone`,
    DELETE_BILLING_MILESTONE: (milestoneId) => `api/project/billing-milestone/${milestoneId}`,
    UPDATE_BOQ_PRICE: (bidId, boqId) => `api/bid/${bidId}/boq/${boqId}/price`,
    SAVE_BILLING_MILESTONES: "api/project/billing-milestone",
};

/**
 * Build a /bid/list URL with optional status + pagination query params.
 * The backend now expects each section to filter via `status`:
 *   - Active Requests   → "awaiting-contractor-confirmation"
 *   - Submitted&Awarded → "proposed-to-client,approved-by-contractor"
 *   - Archived          → "archived"
 *
 * `status` may be a string ("a") or an array (["a", "b"]) — arrays are
 * joined with commas.
 */
const buildBidListUrl = ({ status, page, perPage, managedBy, type } = {}) => {
    const params = new URLSearchParams();
    if (page != null) params.set("page", String(page));
    if (perPage != null) params.set("per_page", String(perPage));
    // Scope type filter — "design" | "execution". "All" is expressed by
    // omitting the param entirely, so only set it when truthy.
    if (type) params.set("type", type);
    if (status != null && status !== "") {
        const value = Array.isArray(status) ? status.filter(Boolean).join(",") : status;
        if (value) params.set("status", value);
    }
    if (managedBy != null && managedBy !== "") {
        const value = Array.isArray(managedBy)
            ? managedBy.filter(Boolean).join(",")
            : managedBy;
        if (value) params.set("managed_by", value);
    }
    const qs = params.toString();
    return qs ? `${RFQ_ENDPOINTS.GET_BID_LIST}?${qs}` : RFQ_ENDPOINTS.GET_BID_LIST;
};

/**
 * Get RFQs/bids for the contractor, filtered by `status` on the backend.
 * `options` is `{ status, page, perPage }`; all are optional.
 */
export const getBidList = async (options = {}) => {
    try {
        const url = buildBidListUrl(options);
        const response = await getApiData(url, "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching bid list:", error);
        throw error;
    }
};

/**
 * Active Requests — single-status filter, paginated.
 */
export const getActiveRequestBids = async (page = 1, perPage = 20, managedBy, type) => {
    return getBidList({
        status: "awaiting-contractor-confirmation",
        page,
        perPage,
        managedBy,
        type,
    });
};

/**
 * Proposed to client — status=proposed-to-client.
 */
export const getSubmittedBids = async (page = 1, perPage = 20, managedBy, type) => {
    return getBidList({
        status: "proposed-to-client",
        page,
        perPage,
        managedBy,
        type,
    });
};

/**
 * Submitted — status=awaiting-project-pricing (design bids the contractor
 * has submitted, now awaiting Reno pricing).
 */
export const getProjectPricingBids = async (page = 1, perPage = 20, managedBy, type) => {
    return getBidList({
        status: "awaiting-project-pricing",
        page,
        perPage,
        managedBy,
        type,
    });
};

/**
 * Awarded — status=approved-by-contractor.
 */
export const getAwardedBids = async (page = 1, perPage = 20, managedBy, type) => {
    return getBidList({
        status: "approved-by-contractor",
        page,
        perPage,
        managedBy,
        type,
    });
};

/**
 * @deprecated — kept temporarily so any older caller keeps working.
 * New code should use `getSubmittedBids` or `getAwardedBids`.
 */
export const getSubmittedAwardedBids = async (page = 1, perPage = 20) => {
    return getBidList({
        status: ["proposal", "approved-by-contractor"],
        page,
        perPage,
    });
};

/**
 * Archived bids — own paginated list.
 */
export const getArchivedBids = async (page = 1, perPage = 20, managedBy, type) => {
    return getBidList({ status: "archived", page, perPage, managedBy, type });
};

/**
 * Contractor bid stats — counts by status/type for the RFQ list header.
 * @returns {Promise} { data: { awaiting_confirmation, approved_by_contractor,
 *   proposed_to_client, archived, ready_to_start, ongoing, design_count,
 *   execution_count, total_count } }
 */
export const getBidStats = async () => {
    try {
        const response = await getApiData(RFQ_ENDPOINTS.GET_BID_STATS, "GET", {});
        return response?.data || null;
    } catch (error) {
        console.error("Error fetching bid stats:", error);
        return null;
    }
};

/**
 * Get bid details by project ID and bid ID
 * @param {string} projectId - Project ID
 * @param {string} bidId - Bid ID
 * @param {string} grouping - Optional grouping parameter (e.g., 'by_category')
 * @returns {Promise} API response
 */
export const getBidDetails = async (projectId, bidId, grouping = null) => {
    try {
        const response = await getApiData(RFQ_ENDPOINTS.GET_BID_DETAILS(projectId, bidId, grouping), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching bid details:", error);
        throw error;
    }
};

/**
 * Get project room BOQs for pricing
 * @param {string} projectId - Project ID
 * @returns {Promise} API response
 */
export const getProjectRoomBOQs = async (projectId) => {
    try {
        const response = await getApiData(RFQ_ENDPOINTS.GET_PROJECT_ROOM_BOQS(projectId), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching project room BOQs:", error);
        throw error;
    }
};

/**
 * Get billing milestones for a bid
 * @param {string} bidId - Bid ID
 * @returns {Promise} API response
 */
export const getBillingMilestones = async (bidId) => {
    try {
        const response = await getApiData(RFQ_ENDPOINTS.GET_BILLING_MILESTONES(bidId), "GET", {});
        return response;
    } catch (error) {
        console.error("Error fetching billing milestones:", error);
        throw error;
    }
};

/**
 * Delete a billing milestone
 * @param {string} milestoneId - Milestone ID
 * @returns {Promise} API response
 */
export const deleteBillingMilestone = async (milestoneId) => {
    try {
        const response = await getApiData(RFQ_ENDPOINTS.DELETE_BILLING_MILESTONE(milestoneId), "DELETE", {});
        return response;
    } catch (error) {
        console.error("Error deleting billing milestone:", error);
        throw error;
    }
};

/**
 * Update BOQ price and quantity
 * @param {string} bidId - Bid ID
 * @param {string} boqId - BOQ ID
 * @param {number} quotedPrice - Quoted price (or manpower_rate for Activities)
 * @param {number} quantity - Quantity (or days for Activities)
 * @param {string} type - BOQ type (Materials or Activities)
 * @returns {Promise} API response
 */
export const updateBOQPrice = async (bidId, boqId, quotedPrice, quantity = null, type = null) => {
    try {
        const payload = {
            quoted_price: quotedPrice
        };

        if (type === "Activities") {
            // For Activities, send days and manpower_rate
            if (quantity !== null) {
                payload.days = quantity;
            }
            payload.manpower_rate = quotedPrice;
        } else {
            // For Materials, send quantity and quoted_price
            if (quantity !== null) {
                payload.quantity = quantity;
            }
        }

        const response = await getApiData(RFQ_ENDPOINTS.UPDATE_BOQ_PRICE(bidId, boqId), "POST", payload);
        return response;
    } catch (error) {
        console.error("Error updating BOQ price:", error);
        throw error;
    }
};

/**
 * Save billing milestones
 * @param {Array} proposalIds - Array of proposal IDs
 * @param {Array} milestones - Array of milestone objects
 * @returns {Promise} API response
 */
export const saveBillingMilestones = async (proposalIds, milestones) => {
    try {
        const response = await getApiData(RFQ_ENDPOINTS.SAVE_BILLING_MILESTONES, "POST", {
            proposal_ids: proposalIds,
            milestones: milestones
        });
        return response;
    } catch (error) {
        console.error("Error saving billing milestones:", error);
        throw error;
    }
};
